import { useEffect, useRef } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
	LayoutDashboard,
	Store,
	FolderKanban,
	Users,
	LogOut,
	X,
	Tags,
	BarChart3,
	FileText,
	MessageCircle,
	MessageSquare,
	Send,
	Bell,
	Search,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../lib/auth-context";
import { ThemeSwitcher } from "../ui/theme-switcher";
import { ReleaseBadge } from "../ui/release-badge";

const navigation = [
	{ name: "Dashboard", href: "/admin", icon: LayoutDashboard },
	{ name: "Analytics", href: "/admin/analytics", icon: BarChart3 },
	{ name: "Pros", href: "/admin/pros", icon: Store },
	{ name: "Projects", href: "/admin/projects", icon: FolderKanban },

	{ name: "Users", href: "/admin/users", icon: Users },
	{ name: "Taxonomy", href: "/admin/taxonomy", icon: Tags },
	{ name: "Blogs", href: "/admin/blogs", icon: FileText },
	{ name: "WhatsApp", href: "/admin/whatsapp", icon: MessageCircle },
	{ name: "Communications", href: "/admin/communications", icon: Send },
	{ name: "Notification Test", href: "/admin/notifications/test", icon: Bell },
	{ name: "Feedback", href: "/admin/feedback", icon: MessageSquare },
	{ name: "Search", href: "/admin/search", icon: Search },
];

type AdminSidebarProps = {
	isOpen: boolean;
	onClose: () => void;
};

export function AdminSidebar({ isOpen, onClose }: AdminSidebarProps) {
	const { signOut, user } = useAuth();
	const routerState = useRouterState();
	const currentPath = routerState.location.pathname;
	const sidebarRef = useRef<HTMLDivElement>(null);

	// Escape key closes sidebar on mobile
	useEffect(() => {
		if (!isOpen) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [isOpen, onClose]);

	// Auto-focus sidebar when opened on mobile
	useEffect(() => {
		if (isOpen) {
			const firstLink =
				sidebarRef.current?.querySelector<HTMLAnchorElement>("nav a");
			firstLink?.focus();
		}
	}, [isOpen]);

	const handleNavClick = () => {
		onClose();
	};

	return (
		<div
			ref={sidebarRef}
			className={cn(
				"fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-background-subtle border-r border-border-default transition-transform duration-300 ease-in-out lg:static lg:translate-x-0",
				isOpen ? "translate-x-0" : "-translate-x-full",
			)}
		>
			{/* Logo */}
			<div className="flex h-16 items-center justify-between px-6 border-b border-border-default">
				<div className="flex items-center gap-2">
					<span className="text-xl font-bold text-foreground-default">
						Admin Panel
					</span>
					<ReleaseBadge />
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close sidebar"
					className="p-2 text-foreground-muted hover:text-foreground-default lg:hidden"
				>
					<X className="h-5 w-5" />
				</button>
			</div>

			{/* Navigation */}
			<nav className="flex-1 space-y-1 px-3 py-4">
				{navigation.map((item) => {
					const isActive =
						item.href === "/admin"
							? currentPath === "/admin"
							: currentPath.startsWith(item.href);
					return (
						<Link
							key={item.name}
							to={item.href}
							onClick={handleNavClick}
							aria-current={isActive ? "page" : undefined}
							className={cn(
								"group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors",
								isActive
									? "bg-primary-100 text-primary-700"
									: "text-foreground-muted hover:bg-background-muted hover:text-foreground-default",
							)}
						>
							<item.icon
								className={cn(
									"mr-3 h-5 w-5 flex-shrink-0",
									isActive
										? "text-primary-600"
										: "text-foreground-subtle group-hover:text-foreground-muted",
								)}
							/>
							{item.name}
						</Link>
					);
				})}
			</nav>

			{/* User section */}
			<div className="border-t border-border-default p-4">
				<div className="flex items-center">
					<div className="flex-shrink-0">
						<div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
							<span className="text-sm font-medium text-primary-700">
								{user?.name?.charAt(0).toUpperCase() || "A"}
							</span>
						</div>
					</div>
					<div className="ml-3 flex-1 min-w-0">
						<p className="text-sm font-medium text-foreground-default truncate">
							{user?.name}
						</p>
						<p className="text-xs text-foreground-muted truncate">
							{user?.email}
						</p>
					</div>
					<ThemeSwitcher variant="icon" size="sm" className="ml-1" />
					<button
						type="button"
						onClick={signOut}
						className="ml-1 p-1 text-foreground-subtle hover:text-foreground-default transition-colors"
						title="Sign out"
					>
						<LogOut className="h-5 w-5" />
					</button>
				</div>
			</div>
		</div>
	);
}
